import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Observable } from 'rxjs/Observable';

import * as moment from 'moment';

import { DealStatus, OpportunityLostReason, OpportunityStatus, Project, ProjectServiceType, ProjectStatus, VelocityProducts } from './project.model';
import { Task } from '../../shared/tasks/task.model';
import { Attachment } from '../../shared/attachments/attachment.model';

import { EnumTextValuesPipe } from '../../shared/pipes/enum-keys-text.pipe';

@Injectable()
export class ProjectService implements OnDestroy {
  keepSubscriptionsAlive = true;

  opportunityStatusTypes = OpportunityStatus;
  opportunityStatusTypeOptions: any;

  dealStatusTypes = DealStatus;
  dealStatusTypeOptions: any;

  projectServiceTypes = ProjectServiceType;
  projectServiceTypesOptions: any;

  velocityProductTypes = VelocityProducts;
  velocityProductTypesOptions: any;

  opportunityLostReasons = OpportunityLostReason;
  opportunityLostReasonOptions: any;
  opportunityLostReasonDropdownOptions: any = this.enumTextValuesPipe.transform(OpportunityLostReason, [ '' ]);

  projectStatusTypes = ProjectStatus;
  projectStatusOptions: any;

  constructor(private readonly httpClient: HttpClient,
              private readonly enumTextValuesPipe: EnumTextValuesPipe) {
  }

  ngOnDestroy() {
    this.keepSubscriptionsAlive = false;
  }

  setDropdownOptions() {
    this.setOpportunityStatusTypeOptions();
    this.setOpportunityLostReasonOptions();
    this.setDealStatusTypeOptions();
    this.setProjectServiceTypeOptions();
    this.setVelocityProductTypesOptions();
    this.setProjectStatusOptions();
  }

  setOpportunityStatusTypeOptions() {
    this.opportunityStatusTypeOptions = this.enumTextValuesPipe.transform(this.opportunityStatusTypes, [ '' ]);
  }

  setOpportunityLostReasonOptions() {
    this.opportunityLostReasonOptions = this.enumTextValuesPipe.transform(this.opportunityLostReasons, [ '' ]);
  }

  setDealStatusTypeOptions() {
    this.dealStatusTypeOptions = this.enumTextValuesPipe.transform(this.dealStatusTypes, [ '' ]);
  }

  setProjectServiceTypeOptions() {
    this.projectServiceTypesOptions = this.enumTextValuesPipe.transform(this.projectServiceTypes, [ '' ]);
  }

  setVelocityProductTypesOptions() {
    this.velocityProductTypesOptions = this.enumTextValuesPipe.transform(this.velocityProductTypes, [ '' ]);
  }

  setProjectStatusOptions() {
    this.projectStatusOptions = this.enumTextValuesPipe.transform(this.projectStatusTypes, [ '' ]);
  }

  addTaskToProject(project: Project, task: Task) {
    // create a spoof company and use the id - otherwise we get a json circular reference
    const spoofTask = new Task();
    spoofTask._id = task._id;

    // push the spoofed company to the team member
    project.tasks.unshift(spoofTask);

    this.saveProject(project);
  }

  addAttachmentToProject(project: Project, attachment: Attachment) {
    const spoofAttachment = new Attachment();
    spoofAttachment._id = attachment._id;

    // push the spoofed company to the team member
    project.attachments.unshift(spoofAttachment);

    this.saveProject(project);
  }

  saveProject(project: Project) {
    this.editProject(project)
      .takeWhile(() => this.keepSubscriptionsAlive)
      .subscribe(error => console.log(error));
  }


  getProjects(dealType: string = 'all'): Observable<Project[]> {
    if (dealType === 'deals') {
      return this.httpClient.get<Project[]>('/api/projects/deals/');
    } else if (dealType === 'opportunities') {
      return this.httpClient.get<Project[]>('/api/projects/opportunities');
    } else {
      return this.httpClient.get<Project[]>('/api/projects');
    }
  }

  getProjectsByOwner(dealType: string = 'all', owner: string = 'all', serviceOfInterest: string = 'all'): Observable<Project[]> {
    if (dealType === 'deals') {
      return this.httpClient.get<Project[]>(`/api/projects/byOwner/deals/${ serviceOfInterest }/${ owner }`);
    } else if (dealType === 'opportunities') {
      return this.httpClient.get<Project[]>(`/api/projects/byOwner/opportunities/${ serviceOfInterest }/${ owner }`);
    } else {
      return this.httpClient.get<Project[]>(`/api/projects/byOwner/all/${ serviceOfInterest }/${ owner }`);
    }
  }

  findTopNProjects(searchString: string, type: string = 'all', limit: number = 5): Observable<Project[]> {
    if (searchString === undefined || searchString === '') {
      searchString = ' ';
    }

    return this.httpClient.get<Project[]>(`/api/projects/find/top/${ type }/${ searchString }/${ limit }`, {
      headers: { ignoreLoadingBar: '' }
    }).map((projects: Project[]) => {
      return projects;
    }).catch(this.handleError);
  }

  countProjects(projectType: string = 'all', serviceOfInterest: string = 'all'): Observable<number> {
    return this.httpClient.get<number>(`/api/projects/count/${ projectType }/${ serviceOfInterest }`);
  }

  addProject(project: Project): Observable<Project> {
    return this.httpClient.post<Project>('/api/project', project);
  }

  getProjectFromId(id: string): Observable<Project> {
    return this.httpClient.get<Project>(`/api/project/${ id }`).catch(this.handleError);
  }

  getProjectFromIdAndPurchaserStatus(id: string, status: string): Observable<Project> {
    return this.httpClient.get<Project>(`/api/project/getProjectFromIdAndPurchaserStatus/${ id }/${ status }`).catch(this.handleError);
  }

  getProjectsCreatedFromTo(from: string, to: string): Observable<Project[]> {
    return this.httpClient.get<Project[]>(`/api/projects/byDate/${ from }/${ to }`);
  }

  getProjectsCreatedFromToByTypeAndOwner(from: string, to: string, type: string = 'all', owner: string = 'all'): Observable<Project[]> {
    return this.httpClient.get<Project[]>(`/api/projects/byDate/${ from }/${ to }/${ type }/${ owner }`)
      .map((projects: Project[]) => {
        projects.forEach((p: Project) => {
          p[ 'mainContact' ] = p[ 'blueboxContacts' ][ 0 ] ? p[ 'blueboxContacts' ][ 0 ].fullName : '';
          p[ 'firstContact' ] = p[ 'blueboxContacts' ][ 0 ] ? p[ 'blueboxContacts' ][ 0 ].fullName : '';
          p[ 'secondContact' ] = p[ 'blueboxContacts' ][ 1 ] ? p[ 'blueboxContacts' ][ 1 ].fullName : '';
          p[ 'thirdContact' ] = p[ 'blueboxContacts' ][ 2 ] ? p[ 'blueboxContacts' ][ 2 ].fullName : '';
        });

        return projects;
      });
  }

  getProjectsCreatedFromToByServiceOfInterest(from: string, to: string, serviceOfInterest: string = 'all'): Observable<Project[]> {
    return this.httpClient.get<Project[]>(`/api/projects/byProduct/${ from }/${ to }/${ serviceOfInterest }`)
      .map((projects: Project[]) => {
        projects.forEach((p: Project) => {
          p[ 'companyName' ] = p.client && p.client[ 'companyName' ] ? p.client[ 'companyName' ] : '';
          p[ 'opportunitySource' ] = p.client && p.client[ 'opportunitySource' ] ? p.client[ 'opportunitySource' ] : '';

          p[ 'firstContact' ] = p.client && p.client.employees[ 0 ] ? p.client.employees[ 0 ].fullName : '';
          p[ 'secondContact' ] = p.client && p.client.employees[ 1 ] ? p.client.employees[ 1 ].fullName : '';
          p[ 'thirdContact' ] = p.client && p.client.employees[ 2 ] ? p.client.employees[ 2 ].fullName : '';
          p[ 'firstContactEmail' ] = p.client && p.client.employees[ 0 ] ? p.client.employees[ 0 ].email : '';
          p[ 'secondContactEmail' ] = p.client && p.client.employees[ 1 ] ? p.client.employees[ 1 ].email : '';
          p[ 'thirdContactEmail' ] = p.client && p.client.employees[ 2 ] ? p.client.employees[ 2 ].email : '';
          p[ 'firstContactTelephone' ] = p.client && p.client.employees[ 0 ] ? p.client.employees[ 0 ].telephone : '';
          p[ 'secondContactTelephone' ] = p.client && p.client.employees[ 1 ] ? p.client.employees[ 1 ].telephone : '';
          p[ 'thirdContactTelephone' ] = p.client && p.client.employees[ 2 ] ? p.client.employees[ 2 ].telephone : '';

          p[ 'BlueboxContact1' ] = p[ 'blueboxContacts' ][ 0 ] ? p[ 'blueboxContacts' ][ 0 ].fullName : '';
          p[ 'BlueboxContact2' ] = p[ 'blueboxContacts' ][ 1 ] ? p[ 'blueboxContacts' ][ 1 ].fullName : '';
          p[ 'BlueboxContact3' ] = p[ 'blueboxContacts' ][ 2 ] ? p[ 'blueboxContacts' ][ 2 ].fullName : '';

          p[ 'InitialMeeting' ] = p.initialMeetingDate ? moment(p.initialMeetingDate).format('DD/MM/YYYY') : '';

          p[ 'TaskDueDate' ] = p.tasks[ 0 ] ? moment(p.tasks[ 0 ].dueDate).format('DD/MM/YYYY') : '';
          p[ 'TaskSubject' ] = p.tasks[ 0 ] ? p.tasks[ 0 ].subject : '';
          p[ 'TaskStatus' ] = p.tasks[ 0 ] ? p.tasks[ 0 ].status : '';
          p[ 'TaskAssignedTo' ] = p.tasks[ 0 ] ? p.tasks[ 0 ].assignedTo.map(at => at.fullName).join(', ') : '';

          p[ 'CommsDate' ] = p.communications[ 0 ] ? moment(p.communications[ 0 ].commsDate).format('DD/MM/YYYY') : '';
          p[ 'CommsContent' ] = p.communications[ 0 ] ? p.communications[ 0 ].content : '';
        });

        return projects;
      });
  }

  editProject(project: Project): Observable<string> {
    return this.httpClient.put(`/api/project/${ project._id }`, project, { responseType: 'text' });
  }

  deleteProject(project: Project): Observable<string> {
    return this.httpClient.delete(`/api/project/${ project._id }`, { responseType: 'text' });
  }

  getPotentialPurchaserReport(id: string): Observable<Blob> {
    return this.httpClient.get(`/api/project/potentialpurchaser/${ id }`, { responseType: 'blob' }).catch(this.handleError);
  }

  getProjectCommunicationsReportByDate(id: string,
                                       from: string = moment().add('-1', 'day').startOf('day').toISOString(),
                                       to: string = moment().endOf('day').toISOString()): Observable<Blob> {
    return this.httpClient.get(`/api/project/getProjectCommunicationReportByDate/${ id }/${ from }/${ to }`, { responseType: 'blob' }).catch(this.handleError);
  }

  getPitches(from: string, to: string): Observable<Project[]> {
    return this.httpClient.get<Project[]>(`/api/projects/getPitches/${ from }/${ to }`);
  }

  private handleError(error: Response) {
    return Observable.throw(error.statusText);
  }
}



// WEBPACK FOOTER //
// ./client/app/features/projects/project.service.ts